// Start der App: Konfiguration lesen, Inhalt laden (/content/<lang>/<deck>.json), Kern anlegen, Oberfläche starten.
import './style.css';
import { readConfig } from './config.js';
import { durableBrowserStore } from './durable-storage.js';
import { browserStore } from './storage.js';
import { createCore } from './core.js';
import { startUI } from './ui.js';
import { initAnalytics, browserTransport, deviceInfo, track } from './analytics.js';

const config = readConfig();
const urlPilot = new URLSearchParams(location.search).get('pilot');   // Testeinschreibung in einen Pilot (nur für neue Einschreibungen)
if (['c1', 'a1'].includes(urlPilot)) { config.pilot = urlPilot; config.pilotExplicit = true; }
let store, A = null;
const view = document.getElementById('view');
const fail = msg => { track('boot_failed', { message: msg.slice(0, 60) }); if (A) A.flush(true); view.innerHTML = `<div class="card"><h2>Das hat nicht geklappt</h2><p>${msg}</p><p><button id="boot-retry">Neu laden</button></p></div>`; document.getElementById('boot-retry').onclick = () => location.reload(); };

async function getJson(url) {
  const r = await fetch(url, { cache: 'no-cache' });
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return r.json();
}

// Deck wählen: ?deck=<lang>/<deck> in der Adresse (wird gemerkt), sonst das zuletzt gewählte, sonst die Vorgabe
function chooseDeck(index) {
  const valid = d => index.decks.some(x => `${x.lang}/${x.deck}` === d);
  const fromUrl = new URLSearchParams(location.search).get('deck');
  if (fromUrl && valid(fromUrl)) return fromUrl;
  const saved = store.get('sentenzo.app.deck');
  if (saved && valid(saved)) return saved;
  if (config.defaultDeck && valid(config.defaultDeck)) return config.defaultDeck;
  return valid(index.default) ? index.default : `${index.decks[0].lang}/${index.decks[0].deck}`;
}

function showCourses(index, selected) {
  const picker = document.createElement('select');
  picker.id = 'course-select';
  picker.setAttribute('aria-label', 'Lernsprache wählen');
  for (const d of index.decks.filter(d => d.listed !== false || `${d.lang}/${d.deck}` === selected)) {
    picker.add(new Option(d.listed === false ? d.title : d.languageName, `${d.lang}/${d.deck}`));
  }
  picker.value = selected;
  // Full navigation creates a core for the chosen deck; pagehide checkpoints the old session.
  picker.onchange = () => location.assign('/?deck=' + encodeURIComponent(picker.value));
  // Flagge immer mit Sprachnamen (Entscheidung 40), nur für Sprachen mit Kurs
  const lang = selected.split('/')[0], parts = [picker];
  if (['it', 'es', 'fr'].includes(lang)) { const f = document.createElement('span'); f.className = 'flag flag-' + lang; f.setAttribute('aria-hidden', 'true'); parts.unshift(f); }
  document.getElementById('side-lang').replaceChildren(...parts);
}

async function boot() {
  // Historical browser fixtures inspect localStorage directly. Production uses IDB.
  store = import.meta.env.VITE_TEST_STORAGE === 'local' ? browserStore() : await durableBrowserStore();
  const base = startAnalytics();
  let content, index;
  try {
    index = await getJson('/content/index.json');
    const selected = chooseDeck(index);
    // Keep the picker usable if a course has not been downloaded and the device is offline.
    showCourses(index, selected);
    const [lang, deck] = selected.split('/');
    if (base) base.course = lang;
    const previous = store.get('sentenzo.app.deck');
    content = await getJson(`/content/${encodeURIComponent(lang)}/${encodeURIComponent(deck)}.json`);
    store.set('sentenzo.app.deck', selected);
    if (previous !== selected) track('course_selected', { previous: previous ? previous.split('/')[0] : null });
  } catch (e) {
    return fail('Der Lerninhalt ließ sich nicht laden. Bitte prüfe die Internetverbindung.');
  }
  const core = createCore({ config, content, store });
  const migrated = core.migratePrototypeOnce();
  if (migrated.length) console.info('Prototyp-Stand übernommen:', migrated);
  // Stimmen ohne Zusatz in Klammern und ohne Dopplungen (mit Folgen tragen mehrere Figuren dieselbe Stimme, z. B. „David Martin (warm)“ und „(ruhig)“)
  if (content.meta.voice) {
    const names = [...new Set(content.meta.voice.replace(/ \([^)]*\)/g, '').split(/, | und /).map(s => s.trim()).filter(Boolean))];
    content.meta.voice = names.length > 1 ? names.slice(0, -1).join(', ') + ' und ' + names.at(-1) : names[0];
  }
  document.querySelectorAll('.foot-legal a').forEach((a, i) => { a.href = [config.legal.impressum, config.legal.datenschutz][i]; });   // Fußzeile steht schon im HTML (auch wenn das Laden scheitert); hier nur abweichende Adressen aus der Konfiguration

  let accountApi = null;
  if (config.online) {
    try { accountApi = await connect(core); } catch (e) { console.error('Konto: Start fehlgeschlagen', e); }
  }
  startUI(core, { account: accountApi, courses: index.decks.filter(d => d.listed !== false) });
  if (A && !accountApi) A.setUser(null);   // ohne Server: gleich mit der Gerätekennung senden

  if (navigator.storage && navigator.storage.persist) navigator.storage.persist().catch(() => {});   // bittet den Browser, die Daten nicht bei Platzmangel zu löschen
  // Installierbar und offline (nur in der gebauten Fassung; im Entwicklungsserver würde der Speicher alte Stände festhalten)
  if (import.meta.env.PROD && 'serviceWorker' in navigator) navigator.serviceWorker.register('/sw.js').catch(() => {});
}

// Mit Supabase verbinden: Client (PKCE: der Login-Link kommt als ?code= zurück, das verträgt sich mit dem #-Router), Konto, Synchronisierung.
// supabase-js wird nur geladen, wenn die App konfiguriert ist (Offline-Modus bleibt klein).
async function connect(core) {
  const [{ createClient }, { Account }, { makeTurnstile }] = await Promise.all([import('@supabase/supabase-js'), import('./account.js'), import('./turnstile.js')]);
  const client = createClient(config.supabaseUrl, config.supabaseKey, {
    auth: { flowType: 'pkce', persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, storageKey: 'sentenzo.auth' },
  });
  const account = new Account({ client, captcha: makeTurnstile(config.turnstileSiteKey, document.getElementById('turnstile')), redirectTo: location.origin + '/' });
  const api = core.attachAccount(account);
  if (core.attachSync) core.attachSync(client);
  // Nicht ewig warten: Ohne Netz startet die App mit dem lokalen Stand und meldet sich an, sobald es geht
  await Promise.race([account.start(), new Promise(r => setTimeout(r, 2500))]);
  const retry = () => { if (!account.user) account.ensureUser(); else account.refreshUser(); };
  window.addEventListener('online', retry);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') retry(); });
  if (A) { A.setUser(account.user); core.on(what => { if (what === 'user' || what === 'status') A.setUser(account.user); }); }
  if (account.urlError) history.replaceState(null, '', '/konto');
  // Rückkehr von Google & Co.: zurück an die Stelle, von der aus es losging
  if (account.oauthBack) { history.replaceState(null, '', account.oauthBack.replace(/^#/, '')); account.oauthBack = null; }
  return api;
}

// Nutzungsdaten (Entscheidung 41): nur mit Schlüssel. Feste Angaben: Kurs, Version, grobe Geräteangaben; je Ereignis die Seite ohne Abfrageteil
// (dort stehen beim Anmelden Codes). ?intern=1 markiert dieses Gerät als intern, ?intern=0 nimmt das zurück.
function startAnalytics() {
  if (!config.posthogKey) return null;
  try { return wireAnalytics(); } catch (e) { console.error('Nutzungsdaten: Start fehlgeschlagen', e); A = null; return null; }   // darf den Start der App nie verhindern
}
function wireAnalytics() {
  const base = { ...deviceInfo(navigator.userAgent, navigator.maxTouchPoints), $lib: 'sentenzo-web', $lib_version: config.appVersion, app_version: config.appVersion,
    schema_version: 1, standalone: !!(window.matchMedia && matchMedia('(display-mode: standalone)').matches), $screen_width: screen.width, $screen_height: screen.height };
  A = initAnalytics({ key: config.posthogKey, host: config.posthogHost, store, transport: browserTransport(), base, dev: config.dev,
    page: () => ({ $current_url: location.origin + location.pathname, $pathname: location.pathname, $host: location.host }) });
  const intern = new URLSearchParams(location.search).get('intern');
  if (intern === '1' || intern === '0') A.setIntern(intern === '1');
  window.addEventListener('error', e => A.error(e.message, e.filename, e.lineno));
  window.addEventListener('unhandledrejection', e => A.error(e.reason && e.reason.message ? e.reason.message : e.reason, 'promise'));
  return base;
}

boot().catch(() => fail('Dein gespeicherter Lernstand ließ sich nicht öffnen. Bitte lade die Seite neu. Deine Daten werden nicht zurückgesetzt.'));
